import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Download, Trash2, FileText, FolderOpen, CreditCard } from 'lucide-react';
import { personnelApi } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import IdCardView from './IdCardView';

const DOC_DEFS = [
  { type: 'vesikalik_foto', label: 'Vesikalık Fotoğraf' },
  { type: 'nufus_kimlik', label: 'Nüfus Cüzdanı / Kimlik' },
  { type: 'diploma', label: 'Diploma' },
  { type: 'sertifika', label: 'Sertifika / Belge' },
  { type: 'is_sozlesmesi', label: 'İş Sözleşmesi' },
  { type: 'saglik_raporu', label: 'Sağlık Raporu' },
  { type: 'adli_sicil', label: 'Adli Sicil Kaydı' },
];

const PersonnelDocumentsModal = ({ personnel, onClose, onChanged }) => {
  const { toast } = useToast();
  const [tab, setTab] = useState('evraklar');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingType, setUploadingType] = useState(null);
  const fileInputs = useRef({});

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await personnelApi.listDocuments(personnel.id);
      setDocs(res.data?.data || []);
    } catch {
      toast({ title: 'Hata', description: 'Belgeler yüklenemedi', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocs(); /* eslint-disable-next-line */ }, [personnel.id]);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      await personnelApi.uploadDocument(personnel.id, fd);
      toast({ title: 'Başarılı', description: 'Belge yüklendi', variant: 'success' });
      await loadDocs();
      onChanged?.();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.message || 'Yüklenemedi', variant: 'destructive' });
    } finally {
      setUploadingType(null);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await personnelApi.deleteDocument(personnel.id, docId);
      await loadDocs();
      onChanged?.();
    } catch {
      toast({ title: 'Hata', description: 'Silinemedi', variant: 'destructive' });
    }
  };

  const docsByType = (type) => docs.filter((d) => d.doc_type === type);
  const totalCount = docs.length;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Başlık */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900">
              {personnel.first_name} {personnel.last_name}
            </h2>
            <span className="text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5 font-medium">
              {totalCount} belge
            </span>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Sekmeler */}
        <div className="flex gap-2 px-6 pt-4">
          <button
            onClick={() => setTab('evraklar')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
              tab === 'evraklar' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <FolderOpen className="h-4 w-4" /> Özlük Evrakları
          </button>
          <button
            onClick={() => setTab('kimlik')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
              tab === 'kimlik' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <CreditCard className="h-4 w-4" /> Kimlik Kartı
          </button>
        </div>

        {/* İçerik */}
        <div className="p-6 overflow-y-auto">
          {tab === 'evraklar' ? (
            loading ? (
              <p className="text-sm text-slate-500 text-center py-8">Yükleniyor...</p>
            ) : (
              <div className="space-y-3">
                {DOC_DEFS.map((def) => {
                  const items = docsByType(def.type);
                  return (
                    <div key={def.type} className="rounded-xl border border-slate-200 p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-800">
                          {def.label}
                          {items.length > 0 && <span className="text-blue-600"> ({items.length})</span>}
                        </p>
                        <>
                          <input
                            ref={(el) => (fileInputs.current[def.type] = el)}
                            type="file"
                            className="hidden"
                            onChange={(e) => { handleUpload(def.type, e.target.files?.[0]); e.target.value = ''; }}
                          />
                          <Button
                            variant="outline" size="sm"
                            disabled={uploadingType === def.type}
                            onClick={() => fileInputs.current[def.type]?.click()}
                          >
                            <Upload className="h-3.5 w-3.5 mr-1.5" />
                            {uploadingType === def.type ? 'Yükleniyor...' : 'Yükle'}
                          </Button>
                        </>
                      </div>
                      {items.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between mt-2 bg-slate-50 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                            <span className="text-sm text-slate-700 truncate">{doc.filename}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-slate-400">{Math.round((doc.size || 0) / 1024)} KB</span>
                            <a href={personnelApi.documentDownloadUrl(personnel.id, doc.id)} className="text-slate-500 hover:text-blue-600">
                              <Download className="h-4 w-4" />
                            </a>
                            <button onClick={() => handleDelete(doc.id)} className="text-slate-500 hover:text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <IdCardView personnel={personnel} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonnelDocumentsModal;
